import { Prisma, ProjectCallsInsightLevel } from "@prisma/client";
import { StandardCallType } from "@/app/types/call/StandardCallType";
import { CallTranscriptMetadata, CallTranscriptSegment } from "@/app/types/call/TranscriptData";
import { InsightSelectMap } from "@/app/types/project/ProjectInsight";

export enum CallAnalysisCategory {
  SUMMARY = "summary",
  PAIN = "painPoints",
  OBJECTIONS = "objections",
  SIGNALS = "buyingSignals",
  VERBATIMS = "verbatims",
  PRODUCT = "featureRequests",
  COMPETITORS = "competitorMentions",
}

export enum AnalysisCategoryLayout {
  STACKED = "stacked",
  SIDE = "side",
}

export enum CallAnalysisSignalLevel {
  LOW = 0,
  MEDIUM = 1,
  HIGH = 2,
}

export const CALL_ANALYSIS_LEVEL_SCORES = {
  [CallAnalysisSignalLevel.LOW]: 33,
  [CallAnalysisSignalLevel.MEDIUM]: 66,
  [CallAnalysisSignalLevel.HIGH]: 100,
} as const;

export function normalizeCallAnalysisSignalLevel(value?: string | number): CallAnalysisSignalLevel {
  if (typeof value === "number") {
    if (value >= 2) return CallAnalysisSignalLevel.HIGH;
    if (value <= 0) return CallAnalysisSignalLevel.LOW;
    return CallAnalysisSignalLevel.MEDIUM;
  }

  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (normalized === "high" || normalized === "2") return CallAnalysisSignalLevel.HIGH;
  if (normalized === "low" || normalized === "0") return CallAnalysisSignalLevel.LOW;
  return CallAnalysisSignalLevel.MEDIUM;
}

export type CallAnalysisSignal = {
  label: string;
  description?: string;
  level?: CallAnalysisSignalLevel;
  quote?: string;
  reason?: string;
};

export const INSIGHT_FOR_MERGE_SELECT = {
  id: true,
  type: true,
  label: true,
  description: true,
  level: true,
  quotes: true,
} as const satisfies InsightSelectMap;

export const INSIGHT_FOR_RECOMMENDATION_SELECT = {
  ...INSIGHT_FOR_MERGE_SELECT,
  callIdsCount: true,
} as const satisfies InsightSelectMap;

export type CallAnalysisMetadataResult = {
  callType?: StandardCallType | string;
  customerIntent?: CallAnalysisSignalLevel;
  mainTopic?: string;
  mainBlocker?: string;
  nextStepDetected?: string;
};

export type CallAnalysisStoredMetadata = CallAnalysisMetadataResult & {
  market?: { model: string; promptVersion: string };
  conversationDynamics?: { model: string; promptVersion: string };
  keyMoments?: { model: string; promptVersion: string };
};

export type ProjectInsightMetadata = {
  model?: string;
};

export type CallMarketAnalysisResult = {
  summary: string;
  metadata: CallAnalysisStoredMetadata;
  painPoints: CallAnalysisSignal[];
  objections: CallAnalysisSignal[];
  buyingSignals: CallAnalysisSignal[];
  verbatims: CallAnalysisSignal[];
  featureRequests: CallAnalysisSignal[];
  competitorMentions: CallAnalysisSignal[];
  scorePain?: number | null;
  scoreObjection?: number | null;
  scoreBuyingSignal?: number | null;
  scoreMarketGlobal?: number | null;
};

export type CallConversationDynamicItem = {
  status: string;
  label: string;
  description: string;
};

export enum CallDynamicsTalkBalance {
  BALANCED = "balanced",
  SLIGHTLY_UNBALANCED = "slightly_unbalanced",
  SELLER_DOMINANT = "seller_dominant",
  PROSPECT_DOMINANT = "prospect_dominant",
  UNCLEAR = "unclear",
}

export enum CallDynamicsProspectEngagement {
  HIGH = "high",
  MEDIUM = "medium",
  LOW = "low",
  VERY_LOW = "very_low",
  UNCLEAR = "unclear",
}

export enum CallDynamicsClarityLevel {
  HIGH = "high",
  MEDIUM = "medium",
  LOW = "low",
  UNCLEAR = "unclear",
}

export enum CallDynamicsMomentum {
  STRONG = "strong",
  MIXED = "mixed",
  STAGNANT = "stagnant",
  DECLINING = "declining",
  UNCLEAR = "unclear",
}

export enum CallDynamicsNextStepClarity {
  CLEAR = "clear",
  PARTIAL = "partial",
  WEAK = "weak",
  ABSENT = "absent",
  UNCLEAR = "unclear",
}

export type CallConversationDynamics = {
  talkBalance: CallConversationDynamicItem;
  prospectEngagement: CallConversationDynamicItem;
  clarityLevel: CallConversationDynamicItem;
  momentum: CallConversationDynamicItem;
  nextStepClarity: CallConversationDynamicItem;
  insights: string[];
  summary: string;
};

export type CallConversationDynamicsResult = {
  conversationDynamicsMetadata: { model: string; promptVersion: string };
  conversationDynamics: CallConversationDynamics;
};

export enum CallKeyMomentImportance {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
  CRITICAL = "critical",
}

export enum CallKeyMomentType {
  CONTEXT_REQUEST = "context_request",
  CLARIFICATION_REQUEST = "clarification_request",
  PAIN_POINT = "pain_point",
  BUYING_SIGNAL = "buying_signal",
  OBJECTION = "objection",
  FEATURE_REQUEST = "feature_request",
  COMPETITOR_MENTION = "competitor_mention",
  FRICTION_SIGNAL = "friction_signal",
  PRICING_TRANSITION = "pricing_transition",
  MOMENTUM_SHIFT = "momentum_shift",
  NEXT_STEP = "next_step",
}

export type CallKeyMoment = {
  time: string;
  type: string;
  label: string;
  description: string;
  speaker: string;
  importance: string;
};

export type CallKeyMomentsResult = {
  keyMomentsMetadata: { model: string; promptVersion: string };
  keyMoments: CallKeyMoment[];
};

export type CallAnalysisResult = CallMarketAnalysisResult &
  CallConversationDynamicsResult &
  CallKeyMomentsResult & {
    metrics?: CallConversationMetricsResult;
    scoreConversationClarity?: number | null;
    scoreProspectEngagement?: number | null;
    scoreNextStepClarity?: number | null;
    scoreCallGlobal?: number | null;
    scoreGlobal?: number | null;
  };

export type CallConversationMetricsOverview = {
  durationSeconds: number;
  speakerCount: number;
  turnCount: number;
  questionCount: number;
  clarificationRequestCount: number;
  interruptionCount: number;
  shortReplyCount: number;
  shortReplyRate: number;
};

export type CallConversationMetricsSpeaker = {
  speakerKey: string;
  displayName: string;
  role: string;
  talkTimeSeconds: number;
  talkRatioPercent: number;
  turnCount: number;
  avgWordsPerTurn: number;
  avgWordsPerMinute: number;
  questionCount: number;
  clarificationCount: number;
  shortReplyCount: number;
  shortReplyRate: number;
};

export type CallConversationMetricsDerived = {
  dominantSpeakerKey?: string;
  dominantSpeakerRatio?: number;
  talkBalanceStatus?: string;
  engagementStatus?: string;
  clarityStatus?: string;
  momentumStatus?: string;
  nextStepStatus?: string;
};

export type CallObservedMetrics = {
  overview: CallConversationMetricsOverview;
  bySpeaker: CallConversationMetricsSpeaker[];
  dominantSpeakerKey?: string;
  dominantSpeakerRatio?: number;
};

export type CallConversationMetricsResult = {
  overview: CallConversationMetricsOverview;
  bySpeaker: CallConversationMetricsSpeaker[];
  derived: CallConversationMetricsDerived;
};

export type CallTranscriptResult = {
  text: string;
  language?: string;
  provider?: string;
  metadata?: CallTranscriptMetadata;
  segments?: CallTranscriptSegment[] | Prisma.JsonValue;
};

export type CallProjectContext = {
  name: string;
  description?: string;
  knownCallTypes?: string[];
};

export type CallContext = {
  name: string;
  type?: string;
  note?: string | null;
};

export type ProjectRecommendationAction = {
  title: string;
  whatResounds: string;
  inactionCost: string;
  whatToDo: string;
};

export type ProjectRecommendationsMetadata = {
  model: string;
};

export enum ProjectCallsRecommendationKind {
  MARKET = "MARKET",
  CALL = "CALL",
}

export type ProjectRecommendationsData = {
  kind: ProjectCallsRecommendationKind;
  actions: ProjectRecommendationAction[];
  metadata: ProjectRecommendationsMetadata;
};

export type ProjectCallsPatternData = {
  id: number;
  projectId: number;
  type: string;
  label: string;
  description: string | null;
  level: string | null;
  score: number | null;
  callsCount: number;
  occurrenceRate: number;
  metadata: unknown;
  createdAt: Date;
  updatedAt: Date;
};

export const PROJECT_CALLS_PATTERN_SELECT = {
  id: true,
  projectId: true,
  type: true,
  label: true,
  description: true,
  level: true,
  score: true,
  callsCount: true,
  occurrenceRate: true,
  metadata: true,
  createdAt: true,
  updatedAt: true,
} satisfies Prisma.ProjectCallsPatternSelect;

export type PatternForMerge = {
  id: number;
  label: string;
  description: string | null;
  level: ProjectCallsInsightLevel | null;
  callsCount: number;
};

export const PATTERN_FOR_MERGE_SELECT = {
  id: true,
  label: true,
  description: true,
  level: true,
  callsCount: true,
} satisfies Prisma.ProjectCallsPatternSelect;

export type ProjectRecommendationItem = {
  title: string;
};

export type ProjectRecommendationsList = {
  actions: ProjectRecommendationItem[];
};
